import { useMutation, useTRPC } from "@luckymenu/api-client/react";
import type { ReorderInput } from "@luckymenu/core";
import { useCallback, useState } from "react";
import { withOrder } from "../menu/cache";
import { type MenuProblem, menuProblemOf } from "../menu/problems";
import { useMenuCache } from "./use-menu";

/**
 * Saves the order after a drag: categories, items within or into a
 * category, add-on groups or kitchen stations. The list moves at once; if
 * the server refuses, it goes back and `problem` says why. Both apps call
 * `reorder` with the full new order of the list that was dragged (see
 * `reorderedIds`). Needs `menu.edit`.
 */
export function useReorder() {
	const trpc = useTRPC();
	const cache = useMenuCache();
	const [problem, setProblem] = useState<MenuProblem | null>(null);
	const mutation = useMutation(
		trpc.staff.catalog.reorder.mutationOptions({
			onMutate: async (input) => {
				await cache.cancel();
				const before = cache.get();
				cache.update((m) => withOrder(m, input));
				return { before };
			},
			onError: (error, _input, context) => {
				const before = (context as { before?: unknown } | undefined)?.before;
				if (before) cache.update(() => before as NonNullable<ReturnType<typeof cache.get>>);
				setProblem(menuProblemOf(error));
			},
			onSettled: () => void cache.refresh(),
		}),
	);
	const reorder = useCallback(
		(input: ReorderInput) => {
			setProblem(null);
			mutation.mutate(input);
		},
		[mutation.mutate],
	);
	return { reorder, problem, clearProblem: useCallback(() => setProblem(null), []) };
}
